import { useSyncExternalStore } from "react";
import { toast } from "sonner";

/** Payload of one `notification:tone` socket push (server pre-resolved the
 *  tone + role targeting — if it reached us, it's meant to ring). */
export interface ToneAlertPayload {
  kind: "assignment" | "status_change";
  toneUrl: string;
  leadId?: number;
  childId?: number;
  leadName?: string;
  statusName?: string;
  /** Sub-status, when the lead/deal sits in one (e.g. "COL issued"). */
  subStatusName?: string;
  timestamp: string;
}

/** "Offer Letter Issued · COL issued" — status, plus sub-status when set. */
export function toneSlotLabel(item: ToneAlertPayload): string | undefined {
  return (
    [item.statusName, item.subStatusName].filter(Boolean).join(" · ") ||
    undefined
  );
}

/**
 * Burst coalescer. Bulk assigns / bulk stage moves emit one socket push per
 * lead within a couple of seconds — ringing N times would be hostile, so
 * events collect for COALESCE_MS after the first arrival and then fire ONCE:
 * one tone, one toast, one popup ("N leads" when the batch is bigger than 1).
 */
const COALESCE_MS = 3000;

let queue: ToneAlertPayload[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

// Currently displayed popup batch (null = popup closed).
let currentBatch: ToneAlertPayload[] | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

function playTone(url: string) {
  try {
    const audio = new Audio(url);
    audio.volume = 0.8;
    // Autoplay can be blocked before the first user gesture — the toast and
    // popup still inform the user, so a silent failure is acceptable.
    void audio.play().catch(() => undefined);
  } catch {
    /* unsupported — toast/popup still show */
  }
}

function flush() {
  timer = null;
  const batch = queue;
  queue = [];
  if (batch.length === 0) return;

  playTone(batch[0].toneUrl);

  if (batch.length === 1) {
    const item = batch[0];
    const what = item.childId != null ? "deal" : "lead";
    const name = item.leadName?.trim();
    const slot = toneSlotLabel(item);
    toast.success(
      name
        ? `${item.kind === "assignment" ? `New ${what}` : `${what === "deal" ? "Deal" : "Lead"} update`}: “${name}”${slot ? ` — ${slot}` : ""}`
        : `You have a new ${what} notification.`,
      { duration: 6000 },
    );
  } else {
    toast.success(`${batch.length} leads need your attention.`, {
      duration: 6000,
    });
  }

  currentBatch = batch;
  notify();
}

/** Socket handler entry point. */
export function enqueueToneAlert(payload: ToneAlertPayload) {
  if (!payload?.toneUrl) return;
  queue.push(payload);
  if (!timer) timer = setTimeout(flush, COALESCE_MS);
}

export function dismissToneAlert() {
  currentBatch = null;
  notify();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSnapshot = () => currentBatch;

/** Reactive view of the popup batch for ToneAlertHost. */
export function useToneAlertBatch(): ToneAlertPayload[] | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
